/**
 * Phase 3.2 — Stranded-user banner.
 *
 * Renders a dismissible yellow banner inside Workspace when the user's
 * embedding lock no longer matches the admin's current default. Provides
 * two self-service options via a "Learn more" dialog:
 *
 *   (A) Re-embed my documents   → POST /api/me/re-embed-self
 *   (B) Delete & re-upload      → closes dialog; user manages from storage
 *
 * Dismissal is session-local (localStorage). The banner re-appears on the
 * user's next visit so the migration suggestion isn't forgotten.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, X, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const DISMISS_KEY = "nexa_stranded_banner_dismissed_v1";

export default function StrandedEmbeddingBanner() {
  const { user, refresh } = useAuth();
  const lock = user?.embedding_lock;
  const isStranded = lock?.is_stranded === true;
  const current = lock?.current_default;

  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1",
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [migrating, setMigrating] = useState(false);

  if (!isStranded || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const reembedSelf = async () => {
    setMigrating(true);
    try {
      const { data } = await api.post("/me/re-embed-self");
      const spawned = data.pipeline_tasks_spawned ?? 0;
      if (spawned > 0) {
        toast.success(
          `Re-embedding ${spawned} file(s) with ${current?.provider}/${current?.model_id} · refresh your storage in ~30s`,
        );
      } else if ((data.total_queued_after ?? 0) > 0) {
        toast.message(`Re-embed already in progress · ${data.total_queued_after} file(s) queued`);
      } else {
        toast.success(`Re-embed complete · ${data.qdrant_wiped ? "vectors wiped" : "no files to migrate"}`);
      }
      setDialogOpen(false);
      // Pull the updated lock; banner disappears once it matches the default.
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-embed failed — try again or contact admin");
    } finally {
      setMigrating(false);
    }
  };

  return (
    <>
      <div
        className="border border-amber-400/50 bg-amber-50 dark:bg-amber-950/20 rounded-sm px-4 py-3 flex items-start gap-3 mb-3"
        data-testid="stranded-embedding-banner"
      >
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <div className="font-semibold text-amber-900 dark:text-amber-200">
            Your account is on an older embedding model
          </div>
          <div className="text-amber-800 dark:text-amber-200/80 mt-0.5 text-xs">
            Vectors:{" "}
            <code className="font-mono">{lock.provider}/{lock.model_id}</code>
            {current && (
              <>
                {" "}· Platform default:{" "}
                <code className="font-mono">{current.provider}/{current.model_id}</code>
              </>
            )}
            . Search quality on new uploads will match your existing files.
          </div>
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-sm border-amber-400 text-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30"
              onClick={() => setDialogOpen(true)}
              data-testid="stranded-banner-learn-more"
            >
              Learn more
            </Button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
          data-testid="stranded-banner-dismiss"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => !o && !migrating && setDialogOpen(false)}>
        <DialogContent data-testid="stranded-banner-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Migrate to the current embedding model?</DialogTitle>
            <DialogDescription asChild>
              <div className="text-sm space-y-3 text-left">
                <p>
                  Your documents were originally embedded with{" "}
                  <code className="font-mono">{lock.provider}/{lock.model_id}</code>.
                  The platform default is now{" "}
                  <code className="font-mono">{current?.provider}/{current?.model_id}</code>.
                  You can migrate in one of two ways:
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="border border-border rounded-sm p-3">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <RefreshCw className="h-4 w-4 text-sky-600" />
                Option A — Re-embed all my documents (recommended)
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                We'll wipe your vector index, clear your lock, and re-process every file
                in the background using the current embedding model. Your files stay
                attached; you'll see them switch to <em>"queued"</em> for ~30 seconds
                then back to <em>ready</em>. No re-upload needed.
              </p>
              <Button
                size="sm"
                className="mt-2 h-8 rounded-sm"
                onClick={reembedSelf}
                disabled={migrating}
                data-testid="stranded-banner-reembed"
              >
                {migrating ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Migrating…</>
                ) : (
                  <><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Re-embed my documents</>
                )}
              </Button>
            </div>

            <div className="border border-border rounded-sm p-3">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <Trash2 className="h-4 w-4 text-rose-600" />
                Option B — Delete & re-upload manually
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                If you prefer a fresh start, delete each file from your storage and
                upload it again. The new uploads will use the current embedding model
                automatically.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="mt-2 h-8 rounded-sm"
                onClick={() => setDialogOpen(false)}
                disabled={migrating}
                data-testid="stranded-banner-manual"
              >
                Got it — I'll do it from storage
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              disabled={migrating}
              data-testid="stranded-banner-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
